/**
 * admin 配置的 FAQ / 引导按钮(quickQuestion) 多语言文本选择 helper。
 *
 * 与 `src/i18n/index.ts` 解耦:那边管 UI 框架文案 (按钮 label/占位提示);
 * 这边只负责从 widget config 返回的 i18n JSON 里按当前 locale 取译文,
 * 找不到译文 (key 缺失或后端尚未翻译) 时 fallback 到源中文字段。
 *
 * 源语言固定 zh:后端 FaqTranslationService 不把 zh 写进 i18n,
 * 所以 locale='zh' 永远走 fallback 路径,正好回退到源字段。
 */
import type { SupportedLocale } from '../types';
import type {
  WidgetConfig,
  WidgetFaq,
  WidgetQuickQuestion,
} from '@digital-cs/shared';

/** quickQuestion 在当前 locale 下的展示文本;无译文 fallback 到源 q.text (中文)。 */
export function pickQuickQuestionText(
  q: WidgetQuickQuestion,
  locale: SupportedLocale,
): string {
  return q.i18n?.[locale] ?? q.text;
}

/**
 * quickQuestion 在当前 locale 下的展示文本;非中文 locale 且无译文时返回 null。
 * 用于渲染层过滤:外文界面混入源中文 chip 比「少展示一条」体验更差(2026-09-18 实测投诉),
 * 后端翻译补齐(quick-question-translation)后条目自动恢复出现,无需改数据。
 */
export function resolveQuickQuestionText(
  q: WidgetQuickQuestion,
  locale: SupportedLocale,
): string | null {
  if (locale === 'zh') return q.text;
  return q.i18n?.[locale] ?? null;
}

/** FAQ 在当前 locale 下的 question/answer;同样按 key fallback 到源。 */
export function pickFaqContent(
  f: WidgetFaq,
  locale: SupportedLocale,
): { question: string; answer: string } {
  const t = f.i18n?.[locale];
  return {
    question: t?.question ?? f.question,
    answer: t?.answer ?? f.answer,
  };
}

/** FAQ 版本:非中文 locale 且无译文时返回 null(与 resolveQuickQuestionText 同一规则)。 */
export function resolveFaqContent(
  f: WidgetFaq,
  locale: SupportedLocale,
): { question: string; answer: string } | null {
  if (locale === 'zh') return { question: f.question, answer: f.answer };
  const t = f.i18n?.[locale];
  if (!t?.question || !t?.answer) return null;
  return { question: t.question, answer: t.answer };
}

/**
 * 客服文案 (welcomeMsg / guideMsg / fallbackMsg 等) 在当前 locale 下的展示值。
 * - locale === 'zh' 或 cfg.i18n 缺失 → 直接走源字段
 * - 命中 cfg.i18n[locale][fieldName] → 返回译文
 * - 译文缺失 → fallback 到源字段;源字段也为空时返回 ''(由调用方自己决定要不要再 fallback 到 UI 文案)
 */
export function pickCsMessage(
  cfg: WidgetConfig,
  fieldName:
    | 'welcomeMsg'
    | 'guideMsg'
    | 'fallbackMsg'
    | 'humanWelcomeMsg'
    | 'name'
    | 'workTime',
  locale: SupportedLocale,
): string {
  const src = cfg[fieldName];
  if (locale === 'zh') return src ?? '';
  const translated = cfg.i18n?.[locale]?.[fieldName];
  if (translated && translated.length > 0) return translated;
  return src ?? '';
}
